import { get, isEmpty, isNil } from "lodash";
import moment from "moment";
import {
  MdArrowBackIos,
  MdOutlineApartment,
  MdOutlineGroups,
  MdOutlineInventory2,
  MdSend,
  MdWarningAmber,
} from "react-icons/md";
import format from "string-template";
import Button from "shared/components/Button";
import Loading from "shared/components/Spin";
import { DATE_FORMAT } from "utils/constants";
import InfoBox from "../Components/InfoBox";
import Section from "../Components/Section";
import { NIL_PLACEHOLDER } from "../constants";
import {
  demographicsFromFields,
  formatCurrency,
  formatRate,
  summaryByCombination,
} from "../helpers";
import ProposalDrawer from "./ProposalDrawer";

const DefinitionList = ({ items }) => (
  <dl className="ac-collectif__definitions">
    {items.map(({ label, value }) => (
      <div className="ac-collectif__definition" key={label}>
        <dt>{label}</dt>
        <dd>{isNil(value) || value === "" ? NIL_PLACEHOLDER : value}</dd>
      </div>
    ))}
  </dl>
);

const View = ({
  translation: T,
  project,
  costDetails,
  costLoading,
  costFailed,
  proposal,
  drawerOpen,
  onOpenDrawer,
  onCloseDrawer,
  onSaveProposal,
  saving,
  onBack,
}) => {
  const fields = get(project, "fields", {});
  const company = get(fields, "company", {});
  const demographics = demographicsFromFields(fields);
  const summaries = summaryByCombination(costDetails);

  return (
    <div className="ac-collectif contact-form">
      <div className="contact-form__content mb-16">
        {costFailed && (
          <InfoBox
            variant="danger"
            icon={<MdWarningAmber size={24} />}
            title={T.OFFER.SIMULATOR.ERROR.TITLE}
            description={T.OFFER.SIMULATOR.ERROR.DESCRIPTION}
          />
        )}

        <Section
          title={T.SUMMARY.CARDS.PRODUCT.TITLE}
          description={T.SUMMARY.CARDS.PRODUCT.DESCRIPTION}
        >
          <DefinitionList
            items={[
              {
                label: T.SUMMARY.CARDS.PRODUCT.INSURER,
                value: T.PRODUCT.INSURER,
              },
              { label: T.SUMMARY.CARDS.PRODUCT.RANGE, value: T.PRODUCT.RANGE },
              {
                label: T.SUMMARY.CARDS.PRODUCT.STRUCTURE,
                value: get(T.STRUCTURES, get(fields, "contributionStructure")),
              },
              {
                label: T.SUMMARY.CARDS.PRODUCT.REGIME,
                value: get(T.REGIMES, get(fields, "socialRegime")),
              },
            ]}
          />
        </Section>

        <Section
          title={T.SUMMARY.CARDS.OFFER.TITLE}
          description={T.SUMMARY.CARDS.OFFER.DESCRIPTION}
        >
          {!costFailed && costLoading && <Loading />}
          {!costFailed && !costLoading && isNil(costDetails) && (
            <InfoBox
              icon={<MdOutlineInventory2 size={24} />}
              title={T.SUMMARY.CARDS.OFFER.EMPTY}
            />
          )}
          {!costLoading &&
            !isNil(costDetails) &&
            get(costDetails, "offers", []).map((item) => (
              <div
                className="ac-collectif__offer-result"
                key={item.combinationId || item.rank}
              >
                <div className="ac-collectif__offer-result-head">
                  <h4>
                    {`${T.RANKS[item.rank]} — ${format(T.LEVEL, {
                      level: item.level,
                    })}`}
                  </h4>
                  <span className="ac-collectif__bracket">
                    {format(T.OFFER.SIMULATOR.RATED_BRACKET, {
                      bracket: item.ratedBracket,
                    })}
                  </span>
                </div>
                <div className="ac-collectif__chips">
                  {isEmpty(item.upsells) ? (
                    <span className="ac-collectif__chip">
                      {T.OFFER.SIMULATOR.NO_REINFORCEMENT}
                    </span>
                  ) : (
                    item.upsells.map((upsell) => (
                      <span className="ac-collectif__chip" key={upsell.id}>
                        {upsell.name}
                      </span>
                    ))
                  )}
                </div>
                <div className="ac-collectif__table-scroll">
                  <table className="ac-collectif__table">
                    <thead>
                      <tr>
                        <th>{T.OFFER.SIMULATOR.TABLE.MEMBER}</th>
                        <th>{T.OFFER.SIMULATOR.TABLE.RATE}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.lines.map((line) => (
                        <tr key={line.member}>
                          <td>{T.MEMBERS[line.member]}</td>
                          <td>
                            {format(T.OFFER.SIMULATOR.TABLE.RATE_VALUE, {
                              rate: formatRate(line.rate),
                              amount: formatCurrency(line.monthlyTaxIncluded),
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>{T.OFFER.SIMULATOR.TABLE.ASSISTANCE}</td>
                        <td>
                          {format(T.OFFER.SIMULATOR.TABLE.MONTHLY_VALUE, {
                            amount: formatCurrency(
                              get(summaries, [
                                item.combinationId || item.rank,
                                "monthlyAssistance",
                              ]),
                            ),
                          })}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ))}
        </Section>

        <Section
          title={T.SUMMARY.CARDS.COMPANY.TITLE}
          description={T.SUMMARY.CARDS.COMPANY.DESCRIPTION}
        >
          <DefinitionList
            items={[
              {
                label: T.COMPANY.FORM.LEGAL_NAME.LABEL,
                value: get(company, "legalName"),
              },
              {
                label: T.COMPANY.FORM.SIRET.LABEL,
                value: get(company, "siret"),
              },
              {
                label: T.COMPANY.FORM.STREET.LABEL,
                value: get(company, "street"),
              },
              {
                label: T.COMPANY.FORM.CITY.LABEL,
                value:
                  `${get(company, "zipCode", "")} ${get(company, "city", "")}`.trim(),
              },
              {
                label: T.COMPANY.FORM.NAF_CODE.LABEL,
                value: get(company, "nafCode"),
              },
              {
                label: T.COMPANY.FORM.LEGAL_FORM.LABEL,
                value: get(company, "legalForm"),
              },
            ]}
          />
        </Section>

        <Section
          title={T.SUMMARY.CARDS.DEMOGRAPHICS.TITLE}
          description={T.SUMMARY.CARDS.DEMOGRAPHICS.DESCRIPTION}
        >
          <DefinitionList
            items={[
              {
                label: T.DEMOGRAPHICS.FORM.CATEGORY.LABEL,
                value: get(T.CATEGORIES, get(demographics, "category")),
              },
              {
                label: T.DEMOGRAPHICS.FORM.HEADCOUNT.LABEL,
                value: get(demographics, "headcount"),
              },
              {
                label: T.DEMOGRAPHICS.FORM.REGIME.LABEL,
                value: get(T.REGIMES, get(demographics, "regime")),
              },
              {
                label: T.DEMOGRAPHICS.FORM.DEPARTMENT.LABEL,
                value: get(demographics, "department"),
              },
              {
                label: T.DEMOGRAPHICS.FORM.EFFECTIVE_DATE.LABEL,
                value: get(demographics, "effectiveDate")
                  ? moment(get(demographics, "effectiveDate")).format(
                      DATE_FORMAT,
                    )
                  : null,
              },
              {
                label: T.DEMOGRAPHICS.FORM.AVERAGE_AGE.LABEL,
                value: get(demographics, "averageAge"),
              },
            ]}
          />
        </Section>
      </div>

      <div className="ac-collectif__actions">
        <Button type="secondary--link" onClick={onBack}>
          <MdArrowBackIos size={16} />
          {T.ACTIONS.PREVIOUS}
        </Button>
        <Button
          type="primary--light"
          onClick={onOpenDrawer}
          disabled={isNil(costDetails)}
        >
          <MdSend size={16} />
          {T.SUMMARY.PROPOSAL.OPEN}
        </Button>
        <Button type="primary" disabled>
          <MdOutlineApartment size={16} />
          {T.SUMMARY.SUBSCRIPTION_CTA}
        </Button>
      </div>
      <p className="ac-collectif__out-of-scope">
        <MdOutlineGroups size={16} />
        {T.SUMMARY.SUBSCRIPTION_OUT_OF_SCOPE}
      </p>

      <ProposalDrawer
        translation={T}
        open={drawerOpen}
        onClose={onCloseDrawer}
        proposal={proposal}
        onSubmit={onSaveProposal}
        saving={saving}
      />
    </div>
  );
};

export default View;
